import React, { useState } from 'react'
import { FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import CustomBadge from '../../../Common/Badge/CustomBadge'
import { badgeColors } from '../../../Common/Color/Color'
import { fontSizes } from '../../../Common/Typography/TypographyStyles'
import { useUpdateAssignedTask } from '../../../../hooks/taskHooks/useUpdateAssignedTask'
import { Task } from '../../../../redux/task/types'
import useConfirmationAlert from '../../../../hooks/componentHooks/useConfirmationAlert'

interface UpdateTaskStatusProps {
  task: Task
}

const UpdateTaskStatus: React.FC<UpdateTaskStatusProps> = ({ task }) => {
  const [selectedValue, setSelectedValue] = useState<string>(task?.status || 'notStarted')
  const [pendingValue, setPendingValue] = useState<string>('')
  const { updateTask } = useUpdateAssignedTask()

  const { ConfirmationAlert, openConfirmationAlert } = useConfirmationAlert()

  const statusMapping: Record<string, { color: string; label: string }> = {
    notStarted: { color: badgeColors.purpleEminence, label: 'Not Started' },
    inProgress: { color: badgeColors.burntOrange, label: 'In Progress' },
    completed: { color: badgeColors.greenSanFelix, label: 'Completed' },
  }

  const handleChange = (event: SelectChangeEvent<string>) => {
    openConfirmationAlert({
      title: 'Update Task',
      text: `Are you sure you want to change the task status to ${statusMapping[event.target.value]?.label}?`,
      onConfirm: async () => {
        setPendingValue(pendingValue)
        setSelectedValue(event.target.value)
        await updateTask({
          teamMemberEmail: task.teamMemberEmail,
          teamMemberWorkdayId: task.teamMemberWorkdayId,
          taskId: task.id,
          status: event.target.value,
        })
      },
    })
  }

  return (
    <FormControl
      variant='outlined'
      margin='none'
      size='small'
      sx={{
        boxShadow: 'inset -10px 0px 23px -9px rgba(128,128,128,0.50)',
        borderRadius: 5,
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
      }}
    >
      {ConfirmationAlert}
      <Select
        value={selectedValue || pendingValue}
        onChange={handleChange}
        renderValue={(selected) => (
          <CustomBadge
            label={statusMapping[selected]?.label || selected}
            size={fontSizes.size12}
            color={statusMapping[selected]?.color || badgeColors.warmYellow}
            width={80}
            rounded
          />
        )}
      >
        {Object.keys(statusMapping).map((status) => (
          <MenuItem key={status} value={status} disabled={selectedValue === 'completed'}>
            <CustomBadge
              label={statusMapping[status]?.label || status}
              size={fontSizes.size12}
              color={statusMapping[status]?.color || badgeColors.warmYellow}
              width={80}
              rounded
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export default UpdateTaskStatus
